import {
  closeCurrentDialog,
  FormSubmitHandler,
  openDialog,
  showNotification,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotEmpty, isNotNil} from 'ramda';

import {useReadCodelistQuery} from '@dms/api/core';
import {useCreateRentalVehicleBlockMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {
  getBlockConflictErrorMessage,
  RentalVehicleBlockConflictDialog,
  RentalVehicleBlockForm,
  type RentalVehicleBlockFormValues,
} from '@dms/vehicle-detail';

import {ApiError, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VEHICLE_BLOCK_CODELIST_ID} from '../constants/vehicleBlockCodelistId';
import {useOverlappingRentalEvents} from '../hooks/useOverlappingRentalEvents';
import type {RentalCalendarPeriod} from '../types/RentalCalendarPeriod';
import {RentalCalendarVehicleSelect} from './RentalCalendarVehicleSelect';
import {RentalPeriodConflictAlert} from './RentalPeriodConflictAlert';

interface RentalCalendarBlockDialogProps extends RequiredTestIdProps {
  vehicleId?: string;
  vehicleTitle?: string;
  slotStart?: Date;
  slotEnd?: Date;
  onPeriodChange?: (period: RentalCalendarPeriod) => void;
  onVehicleChange?: (vehicleId: string) => void;
}

export function RentalCalendarBlockDialog(props: RentalCalendarBlockDialogProps) {
  const [vehicleId, setVehicleId] = useState<string | null>(props.vehicleId ?? null);
  const [period, setPeriod] = useState<RentalCalendarPeriod>({
    from: props.slotStart,
    to: props.slotEnd,
  });

  const updatePeriod = (nextPeriod: RentalCalendarPeriod) => {
    setPeriod(nextPeriod);
    props.onPeriodChange?.(nextPeriod);
  };

  // An empty list while the check is in flight means "unknown", not "clear", so submit waits for it
  const {events: overlappingEvents, isFetching: areOverlapsUnknown} = useOverlappingRentalEvents(
    vehicleId,
    period.from,
    period.to
  );

  const {data: blockReasons} = useReadCodelistQuery({codelistId: VEHICLE_BLOCK_CODELIST_ID});
  const [createRentalVehicleBlock] = useCreateRentalVehicleBlockMutation();

  const reasonOptions =
    blockReasons?.codes?.map((code) => ({value: code.codeId, label: code.name})) ?? [];

  // Must move with the form's `key={vehicleId}` remount, or the period and the overlap check desync
  const handleVehicleChange = (nextVehicleId: string) => {
    setVehicleId(nextVehicleId);
    props.onVehicleChange?.(nextVehicleId);
    updatePeriod({from: props.slotStart, to: props.slotEnd});
  };

  const handleSubmitError = (error: ApiError) => {
    const conflictMessage = getBlockConflictErrorMessage(error);

    if (isNotNil(conflictMessage)) {
      openDialog(<RentalVehicleBlockConflictDialog message={conflictMessage} />, {
        size: 'small',
        'data-testid': suffixTestId('blockDialog-conflict', props),
      });
      return;
    }

    handleApiError(error);
  };

  const handleSubmit: FormSubmitHandler<RentalVehicleBlockFormValues> = async (values) => {
    // Type narrowing for the mutation, not a runtime guard - the form only exists inside the `Show`
    if (isNil(vehicleId)) {
      return;
    }

    await createRentalVehicleBlock({
      vehicleId,
      createRentalVehicleBlockRequestBody: {
        from: values.from.toISOString(),
        to: values.to.toISOString(),
        reasonId: values.reasonId,
        note: values.note,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
      })
      .catch(handleSubmitError);
  };

  const defaultValues =
    isNotNil(props.slotStart) && isNotNil(props.slotEnd)
      ? {from: props.slotStart, to: props.slotEnd, reasonId: ''}
      : undefined;

  return (
    <VStack spacing={4}>
      <RentalCalendarVehicleSelect
        value={vehicleId}
        fallbackId={props.vehicleId}
        fallbackTitle={props.vehicleTitle}
        onChange={handleVehicleChange}
        data-testid={suffixTestId('blockDialog', props)}
      />
      <Show when={isNotNil(vehicleId)}>
        <RentalVehicleBlockForm
          key={vehicleId}
          reasonOptions={reasonOptions}
          defaultValues={defaultValues}
          submitButtonTitle={i18n.t('general.actions.create')}
          onSubmit={handleSubmit}
          onChange={(values) => updatePeriod({from: values.from, to: values.to})}
          isSubmitDisabled={areOverlapsUnknown || isNotEmpty(overlappingEvents)}
          isSubmitLoading={areOverlapsUnknown}
          beforeActions={
            <RentalPeriodConflictAlert
              events={overlappingEvents}
              data-testid={suffixTestId('blockDialog', props)}
            />
          }
        />
      </Show>
    </VStack>
  );
}
